/*
 * Cloudflare Turnstile widget — reusable across all four IONIC forms.
 * Loads the official script once, renders one widget per <Turnstile /> mount,
 * and exposes an imperative reset() so the parent form can consume the
 * single-use token after every submit (success or failure).
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise = null;

function loadTurnstile() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="${SCRIPT_URL}"]`);
    const onLoad = () => resolve(window.turnstile);
    if (existing) {
      if (window.turnstile) return resolve(window.turnstile);
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = onLoad;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Props:
 *   onToken(token: string)      – called with the widget token (or '' on reset/error)
 *   onExpired?()                – optional: token expired, ask user to retry
 *   onError?()                  – optional: widget failed to load or verify
 *   theme?: 'dark' | 'light'    – defaults to 'dark' (matches Ionic site)
 *   className?: string
 * Ref API:
 *   ref.current.reset()         – clear widget + drop token
 */
const Turnstile = forwardRef(function Turnstile(
  { onToken, onExpired, onError, theme = 'dark', className = '' },
  ref,
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onExpiredRef = useRef(onExpired);
  const onErrorRef = useRef(onError);

  // Keep the latest callbacks in refs so the effect below only runs once.
  useEffect(() => { onTokenRef.current = onToken; }, [onToken]);
  useEffect(() => { onExpiredRef.current = onExpired; }, [onExpired]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useImperativeHandle(
    ref,
    () => ({
      reset() {
        if (window.turnstile && widgetIdRef.current !== null) {
          try { window.turnstile.reset(widgetIdRef.current); } catch (_) {}
        }
        onTokenRef.current?.('');
      },
    }),
    [],
  );

  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) {
      // eslint-disable-next-line no-console
      console.warn('[Turnstile] REACT_APP_TURNSTILE_SITE_KEY missing — bot check disabled in this build.');
      onErrorRef.current?.();
      return;
    }
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current) return;
        if (widgetIdRef.current !== null) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onTokenRef.current?.(token),
          'expired-callback': () => {
            onTokenRef.current?.('');
            onExpiredRef.current?.();
          },
          'error-callback': () => {
            onTokenRef.current?.('');
            onErrorRef.current?.();
          },
        });
      })
      .catch(() => {
        onTokenRef.current?.('');
        onErrorRef.current?.();
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme]);

  return (
    <div
      ref={containerRef}
      className={className}
      aria-label="Security verification"
      data-testid="turnstile-widget"
    />
  );
});

export default Turnstile;

/**
 * <HoneypotField /> — invisible field appended to every form. Legit users
 * can't see it (offscreen + aria-hidden + tabindex=-1) but bots that just
 * fill every field will trip it. Backend quarantines any submission where
 * `_hp_website` is non-empty.
 *
 * Usage:
 *   const [hp, setHp] = useState('');
 *   <HoneypotField value={hp} onChange={setHp} />
 *   // send { _hp_website: hp, ...restOfForm }
 */
export const HONEYPOT_FIELD = '_hp_website';

export const HoneypotField = ({ value, onChange }) => {
  const set = useCallback((e) => onChange(e.target.value), [onChange]);
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '-10000px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
      }}
    >
      <label>
        Website (leave blank)
        <input
          type="text"
          name={HONEYPOT_FIELD}
          tabIndex={-1}
          autoComplete="off"
          value={value}
          onChange={set}
          data-testid="honeypot-field"
        />
      </label>
    </div>
  );
};
